import { supabase } from './supabase';

type FormType = 'contact' | 'prayer' | 'newsletter' | 'popup_signup' | 'testimony' | 'booking' | 'partnership' | 'support' | 'custom';

interface FormSubmissionPayload {
  form_type: FormType;
  submitter_name?: string;
  submitter_email?: string;
  submitter_phone?: string;
  content?: string;
  fields?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

const FORM_TYPE_LABELS: Record<FormType, string> = {
  contact: 'Contact Form',
  prayer: 'Prayer Request',
  newsletter: 'Newsletter Signup',
  popup_signup: 'Community Signup',
  testimony: 'Testimony',
  booking: 'Booking',
  partnership: 'Partnership',
  support: 'Support Request',
  custom: 'Form',
};

export async function submitForm(payload: FormSubmissionPayload): Promise<{ success: boolean; id?: string; error?: string }> {
  const { data, error } = await supabase
    .from('form_submissions')
    .insert({
      form_type: payload.form_type,
      submitter_name: payload.submitter_name || null,
      submitter_email: payload.submitter_email || null,
      submitter_phone: payload.submitter_phone || null,
      content: payload.content || null,
      fields: payload.fields || {},
      metadata: {
        referrer: document.referrer || null,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        ...payload.metadata,
      },
    })
    .select('id')
    .maybeSingle();

  if (error) {
    return { success: false, error: error.message };
  }

  if (data?.id) {
    sendPushNotification(payload, data.id).catch(() => {});
  }

  return { success: true, id: data?.id };
}

async function sendPushNotification(payload: FormSubmissionPayload, submissionId: string): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const label = FORM_TYPE_LABELS[payload.form_type] || 'Form';
  const name = payload.submitter_name || payload.submitter_email || 'Anonymous';
  const preview = payload.content ? payload.content.substring(0, 140) : '';

  await fetch(`${supabaseUrl}/functions/v1/send-push-notification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${anonKey}`,
      'apikey': anonKey,
    },
    body: JSON.stringify({
      event_type: 'form_submission',
      target_role: 'admin',
      title: `New ${label} from ${name}`,
      body: preview || `${name} submitted a ${label.toLowerCase()}.`,
      url: `/admin/submissions`,
      tag: `submission-${submissionId}`,
      requireInteraction: true,
      submission_id: submissionId,
      form_type: payload.form_type,
      actions: [
        { action: 'view', title: 'View Submission' },
        { action: 'dismiss', title: 'Dismiss' },
      ],
    }),
  });
}
